import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getbranchData } from "../../../../redux/authentication";
import axios from "axios";
import {
  getInventoryItems,
  getInventoryPurchase,
  getInventorySupplier,
} from "../../../../redux/inventory";
import { useNavigate } from "react-router-dom";

// Helper function to generate location code with format preference ("rackFirst" or "shelfFirst")
// Helper function to generate location codes like "4E-1" or "E4-A"
const generateLocationCode = (loc = {}, format = "rackShelfBin") => {
  const rackNum = loc.rack ? `${loc.rack}` : "";
  const shelfLetter = loc.shelf ? `${loc.shelf.toUpperCase()}` : "";
  const binNum = loc.bin ? `${loc.bin}` : "";

  if (!rackNum && !shelfLetter && !binNum) return "";

  switch (format) {
    case "shelfRackBin":
      // e.g., E4-A (Shelf + Rack + "-" + Bin)
      return `${shelfLetter}${rackNum}${binNum ? `-${binNum}` : ""}`;
    case "rackShelfBin":
    default:
      // e.g., 4E-1 (Rack + Shelf + "-" + Bin)
      return `${rackNum}${shelfLetter}${binNum ? `-${binNum}` : ""}`;
  }
};

// Generate Alphabet options A through Z
const ALPHABET_OPTIONS = Array.from({ length: 26 }, (_, i) =>
  String.fromCharCode(65 + i)
);

const InventoryForm = ({
  editFormData,
  formDataPurchase,
  setInventoryFormData,
  rowIndex,
  toggle,
}) => {
  const navigate = useNavigate();
  const itemsList = useSelector((state) => state.itemsList);
  const auth = useSelector((state) => state.authentication);
  const dispatch = useDispatch();
  const { userData, branchData } = auth; 
  const { supplierData, purchaseData, itemsData } = itemsList;

const defaultLocation = {
    store: "",
    rack: "",
    shelf: "",
    bin: "",
    code: "",
    format: "rackShelfBin", // Options: "rackShelfBin" (4E-1) or "shelfRackBin" (E4-A)
  };

  const [formData, setFormData] = useState(() => {
    const loc = { 
      ...defaultLocation, 
      ...formDataPurchase?.location, 
      ...editFormData?.location 
    };
    return {
      ...formDataPurchase,
      ...editFormData,
      location: {
        ...loc,
        code: loc.code || generateLocationCode(loc, loc.format),
      },
    };
  });

  const [matchBatchExpiry, setMatchBatchExpiry] = useState("");

  function getItemByBatchAndExpiry(enteredBatch, enteredExpiryDate) {
    if (enteredBatch && enteredExpiryDate) {
      const enteredBatchLowercase = enteredBatch?.toLowerCase();
      for (const item of itemsData) {
        const itemBatchLowercase = item?.batch?.toLowerCase();
        if (
          itemBatchLowercase === enteredBatchLowercase &&
          formDataPurchase?.item === item.item
        ) {
          setFormData((prev) => ({ ...prev, expiry: item.expiry }));
          return item;
        }
      }
    }
  }

  function isValidDate(dateString) {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  const validateForm = () => {
    let isValid = true;
    if (!formData.batch) {
      isValid = false;
    }
    if (!isValidDate(formData.expiry)) {
      isValid = false;
    }
    return isValid;
  };

  const clearForm = () => {
    setFormData({
      item: "",
      brandName: "",
      unitPrice: "",
      tax: "",
      mrp: "",
      batch: "",
      expiry: "",
      hsn: "",
      manufacturer: "",
      quantity: "",
      location: defaultLocation,
      category: "",
      minStockLevel: "",
      maxStockLevel: "",
      reorderPoint: "",
      reorderQuantity: "",
      supplierId: "",
      barcode: "",
      notes: "",
      consumable: false,
    });
  };

  useEffect(() => {
    if (editFormData && editFormData.invoice_id) {
      const loc = { ...defaultLocation, ...editFormData.location };
      setFormData({
        ...editFormData,
        location: {
          ...loc,
          code: loc.code || generateLocationCode(loc, loc.format),
        },
      });
      dispatch(
        getInventoryPurchase({
          token: userData.token,
          branch: userData.branch,
          invoice_id: editFormData.invoice_id,
        })
      );
    }
  }, [editFormData]);

  useEffect(() => {
    if (formData?.batch && formData?.expiry) {
      setMatchBatchExpiry(() =>
        getItemByBatchAndExpiry(formData.batch, formData.expiry)
      );
    }
  }, [formData.batch, formData.expiry]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    if (type === "date" && matchBatchExpiry) {
      alert("Please enter New Batch for New Date");
      return;
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedLocation = {
        ...prev.location,
        [name]: name === "shelf" ? value.toUpperCase() : value,
      };

      updatedLocation.code = generateLocationCode(
        updatedLocation, 
        updatedLocation.format
      );

      return {
        ...prev,
        location: updatedLocation,
      };
    });
  };

  // Handler to toggle format between 'rackFirst' and 'shelfFirst'
 const handleFormatToggle = () => {
    setFormData((prev) => {
      const newFormat = prev.location.format === "shelfRackBin" ? "rackShelfBin" : "shelfRackBin";
      const updatedLocation = {
        ...prev.location,
        format: newFormat,
      };
      updatedLocation.code = generateLocationCode(updatedLocation, newFormat);

      return {
        ...prev,
        location: updatedLocation,
      };
    });
  };

  useEffect(() => {
    if (userData?.token) {
      dispatch(
        getbranchData({ token: userData.token, branch: userData.branch })
      );
      dispatch(
        getInventorySupplier({ token: userData.token, branch: userData.branch })
      );
    }
  }, [userData?.token]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm() && editFormData) {
      editSubmitHandler();
      return;
    }

    if (validateForm()) {
      if (formDataPurchase?.batch) {
        if (
          matchBatchExpiry &&
          matchBatchExpiry.expiry === formData.expiry
        ) {
          alert("Continue & Save Purchase - match ");
          if (toggle) toggle();
          setInventoryFormData({
            ...formData,
            productId: matchBatchExpiry.productId,
            stockQty: matchBatchExpiry.quantity,
          });
          return;
        } else {
          alert("Continue & Save Purchase with New Batch");
          if (toggle) toggle();
          setInventoryFormData({ ...formData });
          return;
        }
      } else {
        alert("Continue & Save Purchase");
        if (toggle) toggle();
        setInventoryFormData({ ...formData });
      }
    } else {
      alert("Invalid Form");
      return;
    }
  };

  const editPurchaseHandler = () => {
    if (formDataPurchase?.purchaseId) {
      const postPurchaseData = {
        ...formDataPurchase,
        paymentDetails: {
          ...formDataPurchase.paymentDetails,
          quantity: formData.quantity,
        },
      };

      axios
        .post(
          `${process.env.REACT_APP_CRI_API}edit-purchase`,
          postPurchaseData,
          {
            params: {
              branch: userData.branch,
              purchaseId: formDataPurchase.purchaseId,
            },
            headers: {
              Authorization: `Bearer ${userData.token}`,
            },
          }
        )
        .then((res) => {
          if (res.status === 200) {
            dispatch(
              getInventoryItems({
                token: userData.token,
                branch: userData.branch,
              })
            );
            alert("Edit success");
          }
        })
        .catch((err) => console.log(err));
    }
  };

  const editSubmitHandler = () => {
    axios
      .post(`${process.env.REACT_APP_CRI_API}edit-inventory`, formData, {
        params: {
          branch: userData.branch,
          productId: formData.productId,
        },
        headers: {
          Authorization: `Bearer ${userData.token}`,
        },
      })
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            getInventoryItems({
              token: userData.token,
              branch: userData.branch,
            })
          );
          alert("Edit success");
          editPurchaseHandler();
          clearForm();
          if (toggle) toggle();
        }
      })
      .catch((err) => console.log(err));
  };

  return (
    <Container fluid className="px-0">
      <Form onSubmit={handleSubmit}>
        {/* Row 1: Core Details */}
        <Row>
          <Col md={3}>
            <FormGroup>
              <Label for="batch">
                Batch <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="batch"
                id="batch"
                value={formData.batch || ""}
                onChange={handleChange}
                placeholder="Enter batch"
                required
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="expiry">
                Expiry Date <span className="text-danger">*</span>
              </Label>
              <Input
                type="date"
                name="expiry"
                id="expiry"
                value={formData.expiry || ""}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="category">Category</Label>
              <Input
                type="select"
                name="category"
                id="category"
                value={formData.category || ""}
                onChange={handleChange}
              >
                <option value="">Select Category</option>
                <option value="Oral">Oral</option>
                <option value="Injection / IV">Injection / IV</option>
                <option value="Topical">Topical</option>
                <option value="Surgical">Surgical</option>
                <option value="Skin">Skin</option>
                <option value="Hair">Hair</option>
                <option value="Other">Other</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="brandName">Brand Name</Label>
              <Input
                type="text"
                name="brandName"
                id="brandName"
                value={formData.brandName || ""}
                onChange={handleChange}
                placeholder="Enter brand name"
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Row 2: Secondary Metadata */}
        <Row>
          <Col md={4}>
            <FormGroup>
              <Label for="manufacturer">Manufacturer</Label>
              <Input
                type="text"
                name="manufacturer"
                id="manufacturer"
                value={formData.manufacturer || ""}
                onChange={handleChange}
                placeholder="Enter manufacturer"
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="barcode">Barcode</Label>
              <Input
                type="text"
                name="barcode"
                id="barcode"
                value={formData.barcode || ""}
                onChange={handleChange}
                placeholder="Enter barcode no."
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for="hsn">HSN Code</Label>
              <Input
                type="text"
                name="hsn"
                id="hsn"
                value={formData.hsn || ""}
                onChange={handleChange}
                placeholder="Enter HSN code"
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Row 3: Inventory Levels */}
        <Row>
          <Col md={3}>
            <FormGroup>
              <Label for="minStockLevel">Min Stock Level</Label>
              <Input
                type="number"
                name="minStockLevel"
                id="minStockLevel"
                value={formData.minStockLevel || ""}
                onChange={handleChange}
                placeholder="0"
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="maxStockLevel">Max Stock Level</Label>
              <Input
                type="number"
                name="maxStockLevel"
                id="maxStockLevel"
                value={formData.maxStockLevel || ""}
                onChange={handleChange}
                placeholder="0"
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="reorderPoint">Reorder Point</Label>
              <Input
                type="number"
                name="reorderPoint"
                id="reorderPoint"
                value={formData.reorderPoint || ""}
                onChange={handleChange}
                placeholder="0"
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="reorderQuantity">Reorder Quantity</Label>
              <Input
                type="number"
                name="reorderQuantity"
                id="reorderQuantity"
                value={formData.reorderQuantity || ""}
                onChange={handleChange}
                placeholder="0"
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Location Section */}
      <hr className="my-3" />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold mb-0">Item Location Details</h6>
          <Button
            color="outline-secondary"
            size="sm"
            type="button"
            onClick={handleFormatToggle}
            title="Toggle between 4E-1 and E4-A formats"
          >
            Format: {formData.location?.format === "shelfRackBin" ? "Shelf-Rack-Bin (E4-A)" : "Rack-Shelf-Bin (4E-1)"}
          </Button>
        </div>

        <Row>
          <Col md={3}>
            <FormGroup>
              <Label for="store">Store / Warehouse</Label>
              <Input
                type="select"
                name="store"
                id="store"
                value={formData.location?.store || ""}
                onChange={handleLocationChange}
              >
                <option value="">Select Store</option>
                {branchData?.storeLocations?.length > 0 ? (
                  branchData.storeLocations.map((loc, idx) => (
                    <option key={idx} value={loc}>
                      {loc}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="Store">Store</option>
                    <option value="WH1">WH1</option>
                  </>
                )}
              </Input>
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup>
              <Label for="rack">Rack</Label>
              <Input
                type="number"
                name="rack"
                id="rack"
                min="1"
                value={formData.location?.rack || ""}
                onChange={handleLocationChange}
                placeholder="e.g. 4"
              />
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup>
              <Label for="shelf">Shelf</Label>
              <Input
                type="select"
                name="shelf"
                id="shelf"
                value={formData.location?.shelf || ""}
                onChange={handleLocationChange}
              >
                <option value="">Select</option>
                {ALPHABET_OPTIONS.map((letter) => (
                  <option key={letter} value={letter}>
                    {letter}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup>
              <Label for="bin">Bin / Sub</Label>
              <Input
                type="text"
                name="bin"
                id="bin"
                value={formData.location?.bin || ""}
                onChange={handleLocationChange}
                placeholder="e.g. 1 or A"
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup>
              <Label for="code">Location Code</Label>
              <Input
                type="text"
                name="code"
                id="code"
                value={formData.location?.code || ""}
                readOnly
                placeholder={formData.location?.format === "shelfRackBin" ? "e.g. E4-A" : "e.g. 4E-1"}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Notes & Checkbox */}
        <hr className="my-3" />
        <Row className="align-items-center">
          <Col md={9}>
            <FormGroup className="mb-0">
              <Label for="notes">Notes</Label>
              <Input
                type="text"
                name="notes"
                id="notes"
                value={formData.notes || ""}
                onChange={handleChange}
                placeholder="Enter additional notes..."
              />
            </FormGroup>
          </Col>
          <Col md={3}>
            <FormGroup check className="mt-4 mb-0">
              <Label check>
                <Input
                  type="checkbox"
                  name="consumable"
                  checked={Boolean(formData.consumable)}
                  onChange={handleChange}
                />{" "}
                Consumable
              </Label>
            </FormGroup>
          </Col>
        </Row>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          {toggle && (
            <Button color="secondary" type="button" onClick={toggle}>
              Cancel
            </Button>
          )}
          <Button color="primary" type="submit">
            Submit
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default InventoryForm;